/**
 * Moteur de synchronisation.
 *
 * # Ce qu'il coûte, et pourquoi il est bridé
 *
 * Le polling est le seul poste de consommation significatif du projet. À
 * 5 secondes en continu, deux appareils dépasseraient 34 000 requêtes par jour ;
 * bridé comme ici, la consommation réelle tourne autour de 600 (cf. README).
 *
 * Quatre règles :
 *
 *   - **rien en arrière-plan** — `visibilityState !== 'visible'` arrête tout ;
 *   - **5 s pendant 2 minutes après la dernière interaction**, puis 30 s ;
 *   - **arrêt complet après 10 minutes d'inactivité** ;
 *   - **synchronisation immédiate** au retour au premier plan et au retour du
 *     réseau — c'est ce qui rend le bridage invisible à l'usage.
 *
 * Un poll ne déclenche **aucune écriture** côté serveur.
 *
 * # Ce qu'il ne fait jamais
 *
 * Vider la file sans acquittement. Un timeout, un 429 ou un quota épuisé
 * laissent la file strictement intacte : aucune donnée n'est perdue, quel que
 * soit l'état du service.
 */

import { MAX_BATCH_SIZE } from '@shared/validate.ts';
import { fetchDelta, pushOps, type FailureReason } from '../lib/api.ts';
import * as store from './state.ts';

const ACTIVE_INTERVAL = 5_000;
const IDLE_INTERVAL = 30_000;
/** Durée pendant laquelle on reste en cadence rapide après une interaction. */
const ACTIVE_WINDOW = 2 * 60_000;
/** Au-delà, plus aucun poll : l'application est ouverte mais délaissée. */
const STOP_AFTER = 10 * 60_000;
/** Regroupe les rafales d'actions (cocher plusieurs articles) en un seul envoi. */
const FLUSH_DEBOUNCE = 400;
const MAX_ERROR_BACKOFF = 5 * 60_000;

const QUOTA_MESSAGE =
  'Service temporairement indisponible, tes modifications sont enregistrées localement.';

let token: string | null = null;
let running = false;
let inFlight = false;
let timer: ReturnType<typeof setTimeout> | null = null;
let debounce: ReturnType<typeof setTimeout> | null = null;

let lastInteraction = Date.now();
let pausedUntil = 0;
let consecutiveFailures = 0;
let lastSyncAt: number | null = null;

export function start(householdToken: string): void {
  token = householdToken;
  running = true;
  lastInteraction = Date.now();

  document.addEventListener('visibilitychange', onVisibilityChange);
  window.addEventListener('online', onOnline);
  window.addEventListener('offline', onOffline);
  // Toute action utilisateur relance la cadence rapide.
  document.addEventListener('pointerdown', markInteraction, { passive: true });
  document.addEventListener('keydown', markInteraction, { passive: true });

  store.subscribeToLocalChanges(onLocalChange);

  void syncNow();
}

export function stop(): void {
  running = false;
  clearTimer();

  document.removeEventListener('visibilitychange', onVisibilityChange);
  window.removeEventListener('online', onOnline);
  window.removeEventListener('offline', onOffline);
  document.removeEventListener('pointerdown', markInteraction);
  document.removeEventListener('keydown', markInteraction);
}

export function markInteraction(): void {
  const wasStopped = Date.now() - lastInteraction > STOP_AFTER;
  lastInteraction = Date.now();

  // Sortie de veille : on resynchronise sans attendre le prochain créneau.
  if (wasStopped) void syncNow();
  else schedule();
}

/** Force une synchronisation immédiate (retour au premier plan, reconnexion). */
export async function syncNow(): Promise<void> {
  if (!running || !token || inFlight) return;
  if (document.visibilityState !== 'visible') return;

  inFlight = true;
  setPhase('syncing');

  try {
    const outcome = store.pendingOps().length > 0 ? await flush() : await poll();

    if (outcome === 'ok') {
      consecutiveFailures = 0;
      pausedUntil = 0;
      lastSyncAt = Date.now();
      setPhase('idle');
    }
  } finally {
    inFlight = false;
    schedule();
  }
}

/** Pousse la file, et récupère le delta dans la même requête. */
async function flush(): Promise<'ok' | 'failed'> {
  const pending = store.pendingOps();
  // Au-delà de la taille maximale d'un lot, on envoie une tranche : le reste
  // partira au tour suivant. Rien n'est abandonné.
  const batch = pending.slice(0, MAX_BATCH_SIZE);

  const result = await pushOps(token as string, batch, store.getSnapshot().view.cursor);
  if (!result.ok) return degrade(result.reason, result.retryAfter);

  if (result.value.delta) await store.ingestDelta(result.value.delta);

  // `skipped` n'est PAS acquitté : ces opérations visent une ligne que le
  // serveur ne connaît pas encore, elles seront rejouées. Seules les opérations
  // appliquées et celles définitivement rejetées quittent la file.
  const rejectedOpIds = result.value.rejected
    .map((index) => batch[index]?.op_id)
    .filter((id): id is string => id !== undefined);

  await store.acknowledge(result.value.applied, rejectedOpIds);

  // S'il reste des opérations (tranche suivante, ou `skipped` devenues
  // applicables), on enchaîne immédiatement.
  if (store.pendingOps().length > 0 && store.pendingOps().length < pending.length) {
    queueMicrotask(() => void syncNow());
  }

  return 'ok';
}

async function poll(): Promise<'ok' | 'failed'> {
  const result = await fetchDelta(token as string, store.getSnapshot().view.cursor);
  if (!result.ok) return degrade(result.reason, result.retryAfter);

  await store.ingestDelta(result.value);
  return 'ok';
}

/**
 * Traduit un échec en état visible, sans jamais toucher à la file.
 */
function degrade(reason: FailureReason, retryAfter: number): 'failed' {
  consecutiveFailures += 1;

  switch (reason) {
    case 'unauthorized':
      // Secret ayant tourné, ou lien invalide : réessayer n'y changera rien.
      store.setStatus({
        phase: 'unauthorized',
        message: 'Ce lien de foyer n’est plus valide. Demande un nouveau lien.',
        lastSyncAt,
      });
      running = false;
      clearTimer();
      return 'failed';

    case 'rate_limited':
      pausedUntil = Date.now() + retryAfter * 1000;
      store.setStatus({ phase: 'degraded', message: QUOTA_MESSAGE, lastSyncAt });
      return 'failed';

    case 'quota_exhausted':
      pausedUntil = Date.now() + retryAfter * 1000;
      store.setStatus({ phase: 'degraded', message: QUOTA_MESSAGE, lastSyncAt });
      return 'failed';

    case 'offline':
    case 'server':
      store.setStatus({ phase: 'offline', message: null, lastSyncAt });
      return 'failed';
  }
}

function setPhase(phase: 'syncing' | 'idle'): void {
  store.setStatus({ phase, message: null, lastSyncAt });
}

function schedule(): void {
  clearTimer();
  if (!running || !token) return;

  // Arrêt complet en arrière-plan : c'est la règle qui économise le plus.
  if (document.visibilityState !== 'visible') return;

  const idleFor = Date.now() - lastInteraction;
  if (idleFor > STOP_AFTER) return;

  let delay = idleFor < ACTIVE_WINDOW ? ACTIVE_INTERVAL : IDLE_INTERVAL;

  // Backoff exponentiel après échec, pour ne pas marteler un service en panne.
  if (consecutiveFailures > 0) {
    delay = Math.min(delay * 2 ** Math.min(consecutiveFailures, 6), MAX_ERROR_BACKOFF);
  }

  // Un Retry-After explicite prime toujours.
  delay = Math.max(delay, pausedUntil - Date.now());

  timer = setTimeout(() => void syncNow(), delay);
}

function clearTimer(): void {
  if (timer !== null) clearTimeout(timer);
  timer = null;
}

function onVisibilityChange(): void {
  if (document.visibilityState === 'visible') {
    // Revenir à l'écran est une interaction : cadence rapide, synchro immédiate.
    lastInteraction = Date.now();
    void syncNow();
  } else {
    clearTimer();
  }
}

function onOnline(): void {
  consecutiveFailures = 0;
  pausedUntil = 0;
  void syncNow();
}

function onOffline(): void {
  clearTimer();
  store.setStatus({ phase: 'offline', message: null, lastSyncAt });
}

function onLocalChange(): void {
  lastInteraction = Date.now();

  // On laisse retomber la rafale : cocher cinq articles produit un envoi, pas cinq.
  if (debounce !== null) clearTimeout(debounce);
  debounce = setTimeout(() => {
    debounce = null;
    void syncNow();
  }, FLUSH_DEBOUNCE);
}
